import React, { useEffect, useState } from 'react';
import {
  MdVpnKey, MdContentCopy, MdCheck, MdWhatsapp, MdLockOpen, MdPerson, MdMan, MdWoman,
  MdRefresh, MdBlock,
} from 'react-icons/md';
import api from '../../ui/api';
import { codeText } from './accessCodeText';
import useData from '../useData';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/PageShell';
import { formatPhone } from '../../ui/phone';
import { STUDENT_PORTAL_URL } from '../studentPortal';

/**
 * «حسابات الدخول» — from a pupil's profile in شؤون الطلاب: the pupil's
 * own login and the father's / mother's, each with the exact name to
 * type, and a رمز الدخول handed over on the spot (copy, or WhatsApp to
 * the account's own phone). A login problem is solved where the office
 * already is, without the المستخدمون page.
 *
 * The plain code exists once, in the issue response (stored hashed), so
 * it is shown in place, big, until this window closes.
 */
const WHO = {
  none: { label: 'الطالب', icon: MdPerson, pick: 'الطالب' },
  father: { label: 'الأب', icon: MdMan, pick: 'الأب' },
  mother: { label: 'الأم', icon: MdWoman, pick: 'الأم' },
};

const day = (iso) => (iso ? String(iso).slice(0, 10) : '');
const portalHost = STUDENT_PORTAL_URL.replace(/^https?:\/\//, '');
const digits = (p) => String(p || '').replace(/\D/g, '');

/** What the parent receives: where, who to choose, the name, the code. */
function message(acc, code) {
  const who = WHO[acc.parent_type] || WHO.none;
  return [
    'السلام عليكم،',
    'بيانات الدخول إلى بوابة المتميزون:',
    `الرابط: ${STUDENT_PORTAL_URL}`,
    `من يسجّل الدخول: ${who.pick}`,
    `الاسم: ${acc.name}`,
    `رمز الدخول: ${code}`,
    'يُكتب الرمز مكان رمز التحقق، ويبقى صالحاً حتى تُلغيه الإدارة.',
  ].join('\n');
}

function Account({ acc, code, busy, onIssue, onRevoke, onUnlock, canUnlock, toast }) {
  const who = WHO[acc.parent_type] || WHO.none;
  const Icon = who.icon;
  const [armed, setArmed] = useState(false); // «رمز جديد» asks twice
  const [copied, setCopied] = useState('');

  useEffect(() => {
    if (!armed) return undefined;
    const id = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(id);
  }, [armed]);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1800);
    } catch { toast.error('تعذّر النسخ، انسخه يدوياً'); }
  };

  const hasCode = !!acc.code_hint;
  return (
    <div className={`sl-acc${code ? ' has-new-code' : ''}${acc.is_locked ? ' is-locked' : ''}`}>
      <div className="sl-acc-head">
        <span className={`sl-acc-icon sl-acc-${acc.parent_type}`}><Icon size={20} /></span>
        <div className="sl-acc-main">
          <span className="sl-acc-who">{who.label}</span>
          <span className="sl-acc-name">
            {acc.name}
            <button type="button" className="sl-mini" title="نسخ اسم الدخول" onClick={() => copy(acc.name, 'name')}>
              {copied === 'name' ? <MdCheck size={14} /> : <MdContentCopy size={14} />}
            </button>
          </span>
          <span className="sl-acc-meta">
            {acc.phone ? <bdi dir="ltr">{formatPhone(acc.phone)}</bdi> : <span>بلا رقم هاتف — يدخل برمز الدخول فقط</span>}
            {acc.last_seen_at ? <span>آخر دخول {day(acc.last_seen_at)}</span> : <span>لم يدخل بعد</span>}
          </span>
        </div>
        <div className="sl-acc-state">
          {acc.is_locked
            ? <span className="ui-badge ui-badge-danger" title={acc.lock_reason || ''}>موقوف</span>
            : hasCode
              ? <span className="ui-badge ui-badge-success">رمز فعّال <bdi dir="ltr">{codeText(acc.code_hint)}</bdi></span>
              : acc.phone
                ? <span className="ui-badge ui-badge-neutral">دخول برسالة SMS</span>
                : <span className="ui-badge ui-badge-warning">يحتاج رمز دخول</span>}
        </div>
      </div>

      {hasCode && !code && (
        <div className="sl-acc-codeinfo">
          استُخدم {acc.use_count || 0} مرة
          {acc.last_used_at ? ` · آخر مرة ${day(acc.last_used_at)}` : ''}
          {acc.code_issued_by ? ` · أصدره ${acc.code_issued_by} ${day(acc.code_created_at)}` : ''}
        </div>
      )}

      {code && (
        <div className="sl-code">
          <div className="sl-code-digits" dir="ltr">
            {code.split('').map((d, i) => <span key={i}>{d}</span>)}
          </div>
          <div className="sl-code-actions">
            <Button variant="secondary" onClick={() => copy(message(acc, code), 'msg')}>
              {copied === 'msg' ? <MdCheck size={16} /> : <MdContentCopy size={16} />} نسخ مع التعليمات
            </Button>
            {acc.phone && (
              <a
                className="ui-btn ui-btn-success sl-wa"
                href={`https://wa.me/${digits(acc.phone)}?text=${encodeURIComponent(message(acc, code))}`}
                target="_blank"
                rel="noreferrer"
              >
                <MdWhatsapp size={17} /> إرسال واتساب
              </a>
            )}
          </div>
          <p className="sl-code-note">
            يدخل من <b dir="ltr">{portalHost}</b>: يختار «{who.pick}» ويكتب الاسم أعلاه، ثم هذا الرمز مكان رمز التحقق.
            لن يظهر الرمز مرة أخرى بعد إغلاق النافذة.
          </p>
        </div>
      )}

      <div className="sl-acc-actions">
        {!!acc.is_locked && canUnlock && (
          <Button variant="secondary" onClick={() => onUnlock(acc)} loading={busy === `u${acc.id}`}>
            <MdLockOpen size={16} /> إعادة تفعيل الحساب
          </Button>
        )}
        {!hasCode && !code && (
          <Button onClick={() => onIssue(acc)} loading={busy === `i${acc.id}`} data-guide="login-issue">
            <MdVpnKey size={16} /> إعطاء رمز دخول
          </Button>
        )}
        {(hasCode || code) && (
          <>
            <Button
              variant={armed ? 'danger' : 'secondary'}
              onClick={() => { if (!armed) { setArmed(true); return; } setArmed(false); onIssue(acc); }}
              loading={busy === `i${acc.id}`}
            >
              <MdRefresh size={16} /> {armed ? 'اضغط مرة أخرى: الرمز القديم سيتوقف' : 'رمز جديد'}
            </Button>
            <Button variant="secondary" onClick={() => onRevoke(acc)} loading={busy === `r${acc.id}`}>
              <MdBlock size={16} /> إلغاء الرمز
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function StudentLoginsModal({ student, toast, onClose }) {
  const { data, loading, error, reload } = useData(() => api.studentLogins(student.id), [student.id]);
  const [codes, setCodes] = useState({}); // user id → plain code, this window only
  const [busy, setBusy] = useState('');

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await reload(); } catch (err) { toast.error(err.message); } finally { setBusy(''); }
  };

  const issue = (acc) => run(`i${acc.id}`, async () => {
    const r = await api.issueAccessCode(acc.id, 'من شؤون الطلاب');
    setCodes((c) => ({ ...c, [acc.id]: r.code }));
    toast.success('تم إعطاء رمز الدخول');
  });
  const revoke = (acc) => run(`r${acc.id}`, async () => {
    await api.revokeAccessCode(acc.id);
    setCodes((c) => { const n = { ...c }; delete n[acc.id]; return n; });
    toast.success('تم إلغاء الرمز — يعود الدخول برسالة SMS');
  });
  const unlock = (acc) => run(`u${acc.id}`, async () => {
    await api.unlockUser(acc.id);
    toast.success('تمت إعادة تفعيل الحساب');
  });

  const accounts = data || [];
  return (
    <Modal
      open
      size="md"
      title={`حسابات الدخول — ${student.name}`}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      <div className="sl-list" data-guide="logins-list">
        {loading && !data ? <p className="ui-field-hint">جارٍ التحميل...</p>
          : error ? <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>
            : accounts.length === 0 ? <p className="ui-field-hint">لا توجد حسابات دخول لهذا الطالب.</p>
              : accounts.map((acc) => (
                <Account
                  key={acc.id}
                  acc={acc}
                  code={codes[acc.id]}
                  busy={busy}
                  onIssue={issue}
                  onRevoke={revoke}
                  onUnlock={unlock}
                  canUnlock={student.status === 'active'}
                  toast={toast}
                />
              ))}
      </div>
    </Modal>
  );
}

/**
 * The family's login codes, shown straight in the pupil's profile — no
 * click needed. Accounts without a code are left out; the full window
 * («حسابات الدخول») still issues and revokes codes.
 */
export function StudentCodesStrip({ student, onOpen }) {
  const { data } = useData(() => api.studentLogins(student.id), [student.id]);
  const withCode = (data || []).filter((a) => a.code_hint && !a.is_locked);
  if (!withCode.length) return null;
  return (
    <button type="button" className="sl-strip" onClick={onOpen} title="حسابات الدخول">
      <MdVpnKey size={16} />
      {withCode.map((a) => (
        <span key={a.id} className="sl-strip-item">
          {(WHO[a.parent_type] || WHO.none).label}: <bdi dir="ltr" className="sl-strip-code">{codeText(a.code_hint)}</bdi>
        </span>
      ))}
    </button>
  );
}
